import * as React from 'react';
import styled from '@emotion/styled';
import { colors, respondFrom, breakpoints, css, mixins } from '@styles';
import Image from 'gatsby-image';

import SectionSplit from '@components/common/SectionSplit';
import ButtonLink from '@components/common/ButtonLink';
import AnimationTriggerWaypoint from '@animations/AnimationTriggerWaypoint';
import AnimationHomeSection3 from '@animations/AnimationHomeSection3';
import { useState } from 'react';
import { HomepageBulletsData } from '@content/types/homepage';
import * as utils from '@utils';
import VideoPlayer from '@components/common/VideoPlayer';

const SectionSplitStyled = styled(SectionSplit)`
  position: relative;

  ${respondFrom(
    breakpoints.md,
    css`
      padding-bottom: 50px;

      &::before,
      &::after {
        content: '';
        position: absolute;
        left: 0;
        right: 0;
        bottom: 0;
        margin: auto;
      }

      &::before {
        top: 0;
        background-color: ${colors.green.dark};
        width: 1px;
      }

      &::after {
        width: 9px;
        height: 9px;
        border-radius: 100%;
        ${mixins.gradientGreen};
      }
    `
  )}


  /* animations */
  ${respondFrom(
    breakpoints.md,
    css`
      &::before,
      &::after {
        ${mixins.transitionDefault}
      }
      /* long line */
      &::before {
        transition-duration: 500ms;
        transition-timing-function: ease-in;
        transform-origin: top center;
      }
      /* dot */
      &::after {
        transition-duration: 200ms;
        transition-delay: 500ms;
        transition-timing-function: ease-in;
        transform-origin: center center;
      }
    `
  )}
  &.invisible { /* added by AnimationTriggerWaypoint */
    &::before {
      transform: scaleY(0);
    }
    &::after {
      transform: scale(0);
    }
  }
  /* /animations */
`;

const StyledImage = styled(Image)`
  transform: translate(4.5%, 7%);
`;

const StyledAnimationHomeSection3 = styled(AnimationHomeSection3)`
  position: absolute;
  top: 0;
  left: 0;
  width: 74%;
  height: auto;
  z-index: -1;

  /* transform: translate(11%, -71%); */

  /* !super important for proper scaling */
  svg {
    height: auto;
    width: 100%;
  }
`;

interface SplitSectionProps {
  className?: string;
  data: HomepageBulletsData;
  langcode: string;
  urls: Array<string>;
}

const SplitSection1: React.FC<SplitSectionProps> = props => {
  const [visibility, setVisibility] = useState(false);

  return (
    <AnimationTriggerWaypoint onChange={setVisibility}>
      <SectionSplitStyled
        contentA={
          <>
            {props.data.video ? (
              <VideoPlayer
                image={props.data.image.childImageSharp}
                imageAlt={props.data.imageAlt}
                videoUrl={props.data.video}
              />
            ) : (
              props.data.image && (
                <Image {...props.data.image.childImageSharp} alt={props.data.imageAlt} />
              )
            )}
            <StyledAnimationHomeSection3 sectionVisible={visibility} />
          </>
        }
        contentB={
          <div>
            {utils.SafeHtml(props.data.paragraph)}

            {props.data.buttonHref &&
              props.data.buttonHref !== '' &&
              props.data.buttonLabel &&
              props.data.buttonLabel !== '' && (
                <ButtonLink
                  to={utils.langLink(props.langcode, props.data.buttonHref)}
                  icon="arrow-right"
                  color="text"
                >
                  {props.data.buttonLabel}
                </ButtonLink>
              )}
          </div>
        }
      >
        {props.children}
      </SectionSplitStyled>
    </AnimationTriggerWaypoint>
  );
};

export default SplitSection1;
